import { useEffect, useMemo, useRef, useState } from 'react';
import { LeaderboardEntry, Puzzle } from '../../shared/types';
import { GameState, ValidationResult, createGameState, applyUndo, applyClear, applyHint, applyReveal, applySetting, computeAutoXs, validate } from '../game';
import { api } from '../api';
import { formatTime, formatTimeShort } from '../utils';
import { getPlayerName, getPlayerToken, savePlayerName } from '../cookie';
import { Board } from './Board';
import { HintsPanel } from './HintsPanel';
import { DifficultyTag } from './DifficultyTag';
import { NewPlayerTag } from './NewPlayerTag';
import { pickRevealedQueens } from '../reveal';
import { computeHints, Hint, HintHighlight } from '../hints';
import { solve, solutionToBoard } from '../solver';

// ---- Shared types ----

interface PreloadedLeaderboard {
  entries: LeaderboardEntry[];
  hiddenCount: number;
  showAll: boolean;
}

// ---- Completion form ----

interface CompletionFormProps {
  puzzleId: number;
  timeMs: number;
  gameState: GameState;
  usedAutoXs: boolean;
  usedHints: boolean;
  usedReveal: boolean;
  onLeaderboard: (highlightId?: number, preloaded?: PreloadedLeaderboard) => void;
}

function CompletionForm({ puzzleId, timeMs, gameState, usedAutoXs, usedHints, usedReveal, onLeaderboard }: CompletionFormProps) {
  const [name, setName] = useState(getPlayerName());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit() {
    if (!name.trim()) { setError('Please enter your name.'); return; }
    setSubmitting(true);
    setError('');
    try {
      savePlayerName(name.trim());
      const { entry } = await api.submitCompletion(puzzleId, {
        playerName: name.trim(),
        playerToken: getPlayerToken(),
        timeMs,
        replay: gameState.events,
        usedAutoXs,
        usedHints,
        usedReveal,
      });
      let data = await api.getLeaderboard(puzzleId, false);
      let showAll = false;
      if (!data.entries.some(e => e.id === entry.id)) {
        data = await api.getLeaderboard(puzzleId, true);
        showAll = true;
      }
      onLeaderboard(entry.id, { entries: data.entries, hiddenCount: data.hiddenCount, showAll });
    } catch (err: any) {
      setSubmitting(false);
      setError(err.message ?? 'Submission failed.');
    }
  }

  return (
    <div className="completion-panel">
      <h3>Puzzle solved!</h3>
      <p className="completion-time">Time: <strong>{formatTime(timeMs)}</strong></p>
      <input
        type="text"
        maxLength={32}
        placeholder="Your name"
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        autoFocus
      />
      <div className="completion-row">
        <button className="btn-primary" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
        <button className="btn-secondary" onClick={() => onLeaderboard()}>Leaderboard</button>
      </div>
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}

// ---- Inline leaderboard ----

interface InlineLeaderboardProps {
  puzzleId: number;
  completedMs: number | null;
  highlightId?: number;
  creatorName?: string;
  onWatchReplay: (entryId: number) => void;
  onPlayAgain?: () => void;
  playAgainLabel?: string;
  preloaded?: PreloadedLeaderboard;
}

function ordinal(n: number): string {
  const v = n % 100;
  const suffix = v >= 11 && v <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${n}${suffix}`;
}

const COLOR_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];
const COLOR_EMOJIS = ['🟥', '🟦', '🟩', '🟧', '🟪', '🟨', '🟩', '🟥', '🟫', '🟩', '🟦', '🟥', '⬜', '🟨'];

function InlineLeaderboard({ puzzleId, completedMs, highlightId, creatorName, onWatchReplay, onPlayAgain, playAgainLabel = 'Play again', preloaded }: InlineLeaderboardProps) {
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(preloaded?.entries ?? null);
  const [hiddenCount, setHiddenCount] = useState(preloaded?.hiddenCount ?? 0);
  const [error, setError] = useState(false);
  const [showAll, setShowAll] = useState(preloaded?.showAll ?? false);
  const [copied, setCopied] = useState(false);
  const [shareText, setShareText] = useState<string | null>(null);
  type LbCache = { entries: LeaderboardEntry[]; hiddenCount: number };
  const cacheRef = useRef<{ first?: LbCache; all?: LbCache }>(
    preloaded
      ? preloaded.showAll
        ? { all: { entries: preloaded.entries, hiddenCount: preloaded.hiddenCount } }
        : { first: { entries: preloaded.entries, hiddenCount: preloaded.hiddenCount } }
      : {}
  );
  const userToggledRef = useRef(false);

  useEffect(() => {
    const key = showAll ? 'all' : 'first';
    const cached = cacheRef.current[key];
    if (cached) {
      setEntries(cached.entries);
      setHiddenCount(cached.hiddenCount);
      return;
    }
    let canceled = false;
    api.getLeaderboard(puzzleId, showAll)
      .then(data => {
        if (canceled) return;
        cacheRef.current[key] = { entries: data.entries, hiddenCount: data.hiddenCount };
        setEntries(data.entries);
        setHiddenCount(data.hiddenCount);
        if (!userToggledRef.current && !showAll && highlightId !== undefined && !data.entries.some(e => e.id === highlightId)) {
          setShowAll(true);
        }
      })
      .catch(() => { if (!canceled) setError(true); });
    return () => { canceled = true; };
  }, [puzzleId, showAll, highlightId]);

  useEffect(() => {
    if (highlightId === undefined) return;
    let canceled = false;
    Promise.all([api.getReplay(highlightId), api.getPuzzle(puzzleId)])
      .then(([replayData, puzzleData]) => {
        if (canceled) return;
        // Track the last time each cell was set to 'queen' during replay
        const lastQueenTime = new Map<string, number>();
        let board: (string | null)[][] = Array.from({ length: puzzleData.size }, () => Array(puzzleData.size).fill(null));
        const stack: (string | null)[][][] = [];
        for (const ev of replayData.events) {
          if (ev.type === 'click') {
            const next = board.map(r => [...r]);
            const prev = next[ev.row][ev.col];
            next[ev.row][ev.col] = prev === null ? 'X' : prev === 'X' ? 'queen' : null;
            if (next[ev.row][ev.col] === 'queen') {
              lastQueenTime.set(`${ev.row},${ev.col}`, ev.elapsedMs);
            }
            stack.push(board);
            board = next;
          } else if (ev.type === 'undo') {
            if (stack.length > 0) board = stack.pop()!;
          } else if (ev.type === 'clear') {
            stack.push(board);
            board = Array.from({ length: puzzleData.size }, () => Array(puzzleData.size).fill(null)) as (string | null)[][];
          }
        }
        // Order final queens by when they were placed in their final cell
        const finalQueens: Array<{ colorIdx: number; time: number }> = [];
        for (let r = 0; r < puzzleData.size; r++) {
          for (let c = 0; c < puzzleData.size; c++) {
            if (board[r][c] === 'queen') {
              finalQueens.push({
                colorIdx: COLOR_LABELS.indexOf(puzzleData.colorRegions[r][c]),
                time: lastQueenTime.get(`${r},${c}`) ?? 0,
              });
            }
          }
        }
        finalQueens.sort((a, b) => a.time - b.time);
        const first3 = finalQueens.slice(0, 3).map(q => COLOR_EMOJIS[q.colorIdx] ?? '⬜').join(' ');
        const url = `${window.location.origin}/replay/${puzzleId}/${highlightId}`;
        const aids = [replayData.usedAutoXs && 'Auto Xs', replayData.usedHints && 'Hints'].filter(Boolean);
        const assistNote = aids.length ? ` (with ${aids.join(' + ')})` : '';
        setShareText(`Queens #${puzzleData.name} | ${formatTimeShort(replayData.timeMs)}${assistNote}\nFirst 👑s: ${first3}\n${url}`);
      })
      .catch(() => {});
    return () => { canceled = true; };
  }, [highlightId, puzzleId]);

  function handleShare() {
    if (!shareText) return;
    if (navigator.share) {
      navigator.share({ text: shareText }).catch(() => {});
      return;
    }
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareText).then(done).catch(copyViaExecCommand);
    } else {
      copyViaExecCommand();
    }
    function copyViaExecCommand() {
      const el = document.createElement('textarea');
      el.value = shareText!;
      el.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      try { document.execCommand('copy'); done(); } catch {}
      document.body.removeChild(el);
    }
  }

  if (error) return <p className="error">Failed to load leaderboard.</p>;
  if (!entries) return <p className="loading">Loading leaderboard…</p>;

  return (
    <div className="completion-panel">
      <h3>Leaderboard</h3>
      {creatorName !== undefined && (
        <p className="completion-time">By: <strong>{creatorName}</strong></p>
      )}
      {completedMs !== null && (
        <p className="completion-time">Your time: <strong>{formatTime(completedMs)}</strong></p>
      )}
      {entries.length === 0 ? (
        <p className="empty">No completions yet.</p>
      ) : (
        <table className="leaderboard-inline-table">
          <thead>
            <tr><th>#</th><th>Name</th><th>Time</th><th></th></tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => (
              <tr key={entry.id} className={entry.id === highlightId ? 'highlight' : undefined}>
                <td>{i + 1}</td>
                <td>
                  {entry.playerName}
                  {entry.attemptNumber && entry.attemptNumber > 1 && (
                    <span className="attempt-tag">{ordinal(entry.attemptNumber)} attempt</span>
                  )}
                  {entry.usedAutoXs && (
                    <span className="attempt-tag">Auto Xs</span>
                  )}
                  {entry.usedHints && (
                    <span className="attempt-tag">Hints</span>
                  )}
                  {entry.usedReveal && (
                    <span className="attempt-tag">New Player</span>
                  )}
                </td>
                <td className="time-col">{formatTime(entry.timeMs)}</td>
                <td>
                  <div className="action-col">
                    <a
                      className="btn-replay"
                      href={`/replay/${puzzleId}/${entry.id}`}
                      onClick={e => { if (e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); onWatchReplay(entry.id); }}
                    >▶ Watch</a>
                    {entry.id === highlightId && (
                      <button className={`btn-copy${copied ? ' btn-copy-done' : ''}`} onClick={handleShare}>
                        {copied ? '✓ Copied' : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'-1px',marginRight:'4px'}}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Share</>}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(showAll || hiddenCount > 0) && (
        <button className="btn-toggle-all" onClick={() => { userToggledRef.current = true; setShowAll(v => !v); }}>
          {showAll ? 'Show only first by each player' : `Show ${hiddenCount} hidden submission${hiddenCount === 1 ? '' : 's'}`}
        </button>
      )}
      {onPlayAgain && (
        <button className="btn-primary" onClick={onPlayAgain}>{playAgainLabel}</button>
      )}
    </div>
  );
}

// ---- Game view ----

type Panel =
  | { type: 'none' }
  | { type: 'completion'; timeMs: number; gameState: GameState }
  | { type: 'leaderboard'; timeMs: number | null; highlightId?: number; preloaded?: PreloadedLeaderboard };

interface Props {
  puzzleId: number;
  completedEntryId?: number;
  isSkipped: boolean;
  isLatestPuzzle: boolean;
  /** Queens to start this board with, for a player still inside the new-player window. */
  revealCount: number;
  onBack: () => void;
  onWatchReplay: (entryId: number) => void;
  onComplete: (puzzleId: number, entryId: number) => void;
  onSkip: () => void;
  onResume: () => void;
  onOpenInEditor: () => void;
  onAnalyze: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onRandom?: () => void;
}

export function GameView({ puzzleId, completedEntryId, isSkipped, isLatestPuzzle, revealCount, onBack, onWatchReplay, onComplete, onSkip, onResume, onOpenInEditor, onAnalyze, onPrev, onNext, onRandom }: Props) {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [timerDisplay, setTimerDisplay] = useState('00:00');
  const [timerRunning, setTimerRunning] = useState(false);
  const [autoXsEnabled, setAutoXsEnabled] = useState(() => localStorage.getItem('autoXs') === '1');
  const [hintsEnabled, setHintsEnabled] = useState(() => localStorage.getItem('hints') === '1');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const usedAutoXsRef = useRef(false);
  const usedHintsRef = useRef(false);
  const [hintHighlight, setHintHighlight] = useState<HintHighlight | null>(null);
  // The queens this board starts with, or null when the player is past the new-player window
  // (or the puzzle has no solution to draw them from). Also the flag: a run played on a board
  // with revealed queens is submitted as 'New Player'.
  const [revealed, setRevealed] = useState<Set<string> | null>(null);

  // The assist features (Auto Xs, Hints, Show Solution) stay locked on the newest puzzle until the
  // player has solved it once; afterwards any later attempt (e.g. Play Again) may use them freely.
  const assistsLocked = isLatestPuzzle && !completedEntryId;
  const autoXsOn = autoXsEnabled && !assistsLocked;
  const hintsOn = hintsEnabled && !assistsLocked;

  const [solutions, setSolutions] = useState<number[][] | null>(null);
  const [solIndex, setSolIndex] = useState(0);
  const [solveCapped, setSolveCapped] = useState(false);
  const [solveTimeMs, setSolveTimeMs] = useState<number | null>(null);

  const [boardVisible, setBoardVisible] = useState(() => {
    if (completedEntryId) return false;
    return !isSkipped;
  });
  const [panel, setPanel] = useState<Panel>(() => {
    if (completedEntryId) return { type: 'leaderboard', timeMs: null, highlightId: completedEntryId };
    if (isSkipped) return { type: 'leaderboard', timeMs: null };
    return { type: 'none' };
  });
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    api.getPuzzle(puzzleId).then(p => {
      setPuzzle(p);
      const cells = pickRevealedQueens(p.colorRegions, revealCount, p.id);
      setRevealed(cells.length ? new Set(cells) : null);
      setGameState(freshState(p, cells));
      if (!completedEntryId && !isSkipped) {
        startTimeRef.current = Date.now();
        setTimerRunning(true);
      }
    }).catch(() => setLoadError(true));
  }, [puzzleId]);

  useEffect(() => {
    if (!timerRunning) return;
    const id = setInterval(() => {
      setTimerDisplay(formatTime(Date.now() - startTimeRef.current));
    }, 100);
    return () => clearInterval(id);
  }, [timerRunning]);

  function seedSettings(state: GameState): GameState {
    return applySetting(applySetting(state, 'autoXs', autoXsOn), 'hints', hintsOn);
  }

  /** A board at its starting position: the new player's revealed queens, then the replay seeded
   *  with both assist settings so their *initial* values are recorded, not just later changes. */
  function freshState(p: Puzzle, revealedCells: string[]): GameState {
    return seedSettings(applyReveal(createGameState(p), revealedCells));
  }

  function handleBoardChange(newState: GameState, result: ValidationResult) {
    if (autoXsOn && gameState) {
      const size = newState.board.length;
      outer: for (let r = 0; r < size; r++) {
        for (let c = 0; c < size; c++) {
          if (newState.board[r][c] === 'queen' && gameState.board[r][c] !== 'queen') {
            usedAutoXsRef.current = true;
            break outer;
          }
        }
      }
    }
    setGameState(newState);
    if (result.solved) {
      const elapsed = Date.now() - startTimeRef.current;
      setTimerRunning(false);
      setPanel({ type: 'completion', timeMs: elapsed, gameState: newState });
    }
  }

  function handleApplyHint(h: Hint) {
    if (!gameState || !puzzle) return;
    usedHintsRef.current = true;
    const newState = applyHint(gameState, h.kind as 'eliminate' | 'place', h.cells, puzzle.colorRegions, h.rule);
    handleBoardChange(newState, validate(newState.board, puzzle.colorRegions));
  }

  function handleUndo() {
    if (!gameState) return;
    setGameState(applyUndo(gameState));
  }

  function handleClear() {
    if (!gameState) return;
    // Clear returns the board to its *starting* position, which for a new player still has their
    // revealed queens on it. Clearing them away would take back the help with no way to ask for
    // it again, and leave those cells locked and empty.
    setGameState(applyReveal(applyClear(gameState), revealed ? [...revealed] : []));
  }

  function handleToggleAutoXs() {
    const next = !autoXsEnabled;
    setAutoXsEnabled(next);
    localStorage.setItem('autoXs', next ? '1' : '0');
    // Recorded in the replay: the completion's used_auto_xs flag cannot express a mid-game
    // change, so analysis has to infer the mode from behavior without this.
    setGameState(g => (g ? applySetting(g, 'autoXs', next) : g));
  }

  function handleToggleHints() {
    const next = !hintsEnabled;
    setHintsEnabled(next);
    localStorage.setItem('hints', next ? '1' : '0');
    if (!next) setHintHighlight(null);
    setGameState(g => (g ? applySetting(g, 'hints', next) : g));
  }

  function handlePlayAgain() {
    if (!puzzle) return;
    onResume();
    usedAutoXsRef.current = false;
    usedHintsRef.current = false;
    setHintHighlight(null);
    setGameState(freshState(puzzle, revealed ? [...revealed] : []));
    startTimeRef.current = Date.now();
    setTimerDisplay('00:00');
    setTimerRunning(true);
    setPanel({ type: 'none' });
    setBoardVisible(true);
  }

  function handleSolve() {
    if (!puzzle) return;
    const MAX = 1000;
    const t0 = performance.now();
    const results = solve(puzzle.colorRegions, MAX);
    setSolveTimeMs(performance.now() - t0);
    setSolutions(results);
    setSolIndex(0);
    setSolveCapped(results.length >= MAX);
    setSettingsOpen(false);
  }

  function handleCloseSolver() {
    setSolutions(null);
    setSolIndex(0);
    setSolveCapped(false);
    setSolveTimeMs(null);
  }

  function handleSkip() {
    onSkip();
    setTimerRunning(false);
    setPanel({ type: 'leaderboard', timeMs: null });
    setBoardVisible(false);
  }

  function handleResume() {
    onResume();
    startTimeRef.current = Date.now();
    setTimerDisplay('00:00');
    setTimerRunning(true);
    setPanel({ type: 'none' });
    setBoardVisible(true);
  }

  const showHints = hintsOn && boardVisible && panel.type === 'none' && solutions === null;
  const hints = useMemo(
    () => {
      if (!showHints || !puzzle || !gameState) return [];
      // With Auto Xs on the attacked squares are displayed as Xs, so the engine may treat
      // them as eliminated; without it, only real X marks count.
      const visibleXs = autoXsOn ? computeAutoXs(gameState.board, puzzle.colorRegions) : undefined;
      return computeHints(gameState.board, puzzle.colorRegions, visibleXs);
    },
    [showHints, puzzle, gameState, autoXsOn]
  );

  useEffect(() => { if (!showHints) setHintHighlight(null); }, [showHints]);

  if (loadError) return <p className="error">Failed to load puzzle.</p>;

  return (
    <div id="view-game">
      <header className="game-header">
        <div className="game-header-inner">
          <button className="btn-back" onClick={onBack}>←<span className="hide-mobile"> Puzzles</span></button>
          <h2>
            <span className="game-title-name">{puzzle?.name ?? ''}</span>
            {puzzle?.solutionCount != null && puzzle.solutionCount > 1 && (
              <span className="multi-tag" title="Any valid arrangement counts as a win">
                {puzzle.solutionCount >= 50 ? '50+' : puzzle.solutionCount} solutions
              </span>
            )}
            {/* One tag, not two: on a beginner's board the useful thing to explain is the queens
                already sitting on it, not how long a typical player would take. */}
            {revealed
              ? <NewPlayerTag count={revealed.size} />
              : puzzle?.difficulty && <DifficultyTag difficulty={puzzle.difficulty} />}
          </h2>
          <div className="game-nav">
            <button className="btn-nav" onClick={onPrev} disabled={!onPrev}><span className="show-mobile">◀</span><span className="hide-mobile">Prev</span></button>
            <button className="btn-nav" onClick={onRandom} disabled={!onRandom}><span className="show-mobile">↺</span><span className="hide-mobile">Random</span></button>
            <button className="btn-nav" onClick={onNext} disabled={!onNext}><span className="show-mobile">▶</span><span className="hide-mobile">Next</span></button>
          </div>
        </div>
      </header>
      <main>
        <div className="board-wrapper">
          {panel.type === 'completion' && (
            <CompletionForm
              puzzleId={puzzleId}
              timeMs={panel.timeMs}
              gameState={panel.gameState}
              usedAutoXs={usedAutoXsRef.current}
              usedHints={usedHintsRef.current}
              usedReveal={revealed !== null}
              onLeaderboard={(id, preloaded) => { if (id) onComplete(puzzleId, id); setPanel({ type: 'leaderboard', timeMs: panel.timeMs, highlightId: id, preloaded }); }}
            />
          )}
          {panel.type === 'leaderboard' && (
            <>
              <InlineLeaderboard
                puzzleId={puzzleId}
                completedMs={panel.timeMs}
                highlightId={panel.highlightId}
                creatorName={puzzle?.creatorName}
                onWatchReplay={onWatchReplay}
                onPlayAgain={!boardVisible ? (panel.highlightId === undefined && panel.timeMs === null ? handleResume : handlePlayAgain) : undefined}
                playAgainLabel={panel.highlightId === undefined && panel.timeMs === null ? 'Resume Puzzle' : 'Play again'}
                preloaded={panel.preloaded}
              />
            </>
          )}
          {boardVisible && (
            <>
              <div className="board-top-bar">
                {solutions !== null ? (
                  <>
                    <span className="solver-count">
                      {solutions.length === 0
                        ? 'No solutions found'
                        : <>Solution {solIndex + 1} of {solutions.length}{solveCapped ? ' (capped at 1000)' : ''}</>}
                      {solveTimeMs !== null && <> · {solveTimeMs < 1 ? '<1' : Math.round(solveTimeMs)}ms</>}
                    </span>
                    <div className="board-actions">
                      {solutions.length > 1 && (
                        <>
                          <button className="btn-icon" onClick={() => setSolIndex(i => (i - 1 + solutions.length) % solutions.length)}>‹ Prev</button>
                          <button className="btn-icon" onClick={() => setSolIndex(i => (i + 1) % solutions.length)}>Next ›</button>
                        </>
                      )}
                      <button className="btn-icon" onClick={handleCloseSolver}>✕ Close</button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="timer">{timerDisplay}</span>
                    <div className="board-actions">
                      <button
                        className={`btn-icon${settingsOpen ? ' btn-icon-on' : ''}`}
                        onClick={() => setSettingsOpen(o => !o)}
                      >Help</button>
                      <button className="btn-icon" onClick={handleUndo}>↩ Undo</button>
                      <button className="btn-icon" onClick={handleClear}>✕ Clear</button>
                    </div>
                  </>
                )}
              </div>
              {puzzle && gameState ? (
                <Board
                  puzzle={puzzle}
                  state={solutions !== null && solutions.length > 0
                    ? { ...createGameState(puzzle), board: solutionToBoard(solutions[solIndex], puzzle.size) }
                    : gameState}
                  onStateChange={solutions !== null ? () => {} : handleBoardChange}
                  autoXs={solutions === null && autoXsOn}
                  readOnly={solutions !== null}
                  highlight={solutions === null ? hintHighlight : null}
                  revealed={solutions === null ? revealed : null}
                />
              ) : (
                <p className="loading">Loading…</p>
              )}
              {panel.type === 'none' && solutions === null && (
                <button className="btn-skip" onClick={handleSkip}>Skip and View Leaderboard</button>
              )}
              {showHints && (
                <HintsPanel
                  hints={hints}
                  onHighlight={setHintHighlight}
                  onUse={() => { usedHintsRef.current = true; }}
                  onApply={handleApplyHint}
                />
              )}
            </>
          )}
        </div>
      </main>
      {settingsOpen && (
        <div className="help-modal-overlay" onClick={() => setSettingsOpen(false)}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <h3>Settings</h3>
            {assistsLocked ? (
              <p className="settings-note">Auto Xs, Hints, and Show Solution unlock once you've solved this puzzle.</p>
            ) : (
              <>
                <label className="toggle-row">
                  <span>Auto Xs</span>
                  <span className="toggle-switch">
                    <input type="checkbox" checked={autoXsEnabled} onChange={handleToggleAutoXs} />
                    <span className="toggle-slider" />
                  </span>
                </label>
                <label className="toggle-row">
                  <span>Hints</span>
                  <span className="toggle-switch">
                    <input type="checkbox" checked={hintsEnabled} onChange={handleToggleHints} />
                    <span className="toggle-slider" />
                  </span>
                </label>
                <button className="settings-row" onClick={handleSolve} disabled={!puzzle}>Show Solution</button>
              </>
            )}
            <button className="settings-row" onClick={() => { setSettingsOpen(false); onOpenInEditor(); }}>Open in Editor</button>
            <button className="settings-row" onClick={() => { setSettingsOpen(false); onAnalyze(); }}>Analyze solution paths</button>
            <button className="btn-secondary" style={{ marginTop: '0.25rem' }} onClick={() => setSettingsOpen(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
